import { createContext, useContext } from 'react';
import { useExtensions } from '../../lib/api/hooks.js';
import {
  useRingGroups, useQueues, useIvrs, useMessages, useHoursChecks, useMailboxes, useCallerChecks, useLookups, useInboundRoutes,
} from '../../lib/api/hooks-tms.js';
import type { Destination } from '@arcnet/contracts';

/** The kinds of step a destination can name by id, as the pickers list them. */
type StepType = 'ring_group' | 'queue' | 'ivr' | 'message' | 'hours' | 'caller' | 'lookup' | 'route';

/**
 * Steps that exist only in a call flow's draft (docs/41 §5): offered beside the live ones, and named
 * by the draft's own name, so a key can point at a hunt list made a minute ago and not yet published.
 * A draft's rename of a live step shows here too. Outside a flow editor there are none.
 */
export interface DestinationExtra { type: StepType; id: string; name: string }
export const DestinationExtras = createContext<readonly DestinationExtra[]>([]);

/** Live rows with the draft's additions and renames over them. */
function withExtras<T extends { id: string; name: string }>(
  rows: readonly T[] | undefined, extras: readonly DestinationExtra[], type: StepType,
): Array<{ id: string; name: string }> {
  const mine = extras.filter((x) => x.type === type);
  const renamed = new Map(mine.map((x) => [x.id, x.name]));
  const live = (rows ?? []).map((r) => ({ id: r.id, name: renamed.get(r.id) ?? r.name }));
  const fresh = mine.filter((x) => !live.some((r) => r.id === x.id)).map((x) => ({ id: x.id, name: x.name }));
  return [...live, ...fresh].sort((a, b) => a.name.localeCompare(b.name));
}

const GROUPS: ReadonlyArray<[StepType, string, string]> = [
  ['ring_group', 'Hunt lists', 'Hunt list'],
  ['queue', 'Queues', 'Queue'],
  ['ivr', 'IVR menus', 'IVR menu'],
  ['message', 'Messages', 'Message'],
  ['hours', 'Hours checks', 'Hours check'],
  ['caller', 'Caller checks', 'Caller check'],
  ['lookup', 'Look ups', 'Look up'],
  // Another number's whole flow, hours and all (docs/41 §16).
  ['route', 'Other call flows', 'Call flow'],
];

/** Every place a call can be sent, as one lookup the picker and the label both read. */
function useTargets(tenantId: string) {
  const extras = useContext(DestinationExtras);
  const extensions = useExtensions(tenantId, { limit: 200 });
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);
  const ivrs = useIvrs(tenantId);
  const messages = useMessages(tenantId);
  const hours = useHoursChecks(tenantId);
  const callers = useCallerChecks(tenantId);
  const lookups = useLookups(tenantId);
  const routes = useInboundRoutes(tenantId);
  const boxes = useMailboxes(tenantId);
  const steps: Record<StepType, Array<{ id: string; name: string }>> = {
    ring_group: withExtras(groups.data?.data, extras, 'ring_group'),
    queue: withExtras(queues.data?.data, extras, 'queue'),
    ivr: withExtras(ivrs.data?.data, extras, 'ivr'),
    message: withExtras(messages.data?.data, extras, 'message'),
    hours: withExtras(hours.data?.data, extras, 'hours'),
    caller: withExtras(callers.data?.data, extras, 'caller'),
    lookup: withExtras(lookups.data?.data, extras, 'lookup'),
    route: withExtras(routes.data?.data, extras, 'route'),
  };
  return { extensions: extensions.data?.data ?? [], boxes: boxes.data?.data ?? [], steps };
}

/**
 * Where a call goes.
 *
 * One control for every place a call can be sent, used by IVR options, inbound
 * routes and timeout destinations alike. The alternative — a bespoke selector
 * per screen — is how three screens end up offering three different subsets of
 * the same choices.
 */
export function DestinationPicker({ tenantId, value, onChange, allowNone = false, id }: {
  tenantId: string;
  value: Destination | null;
  onChange: (d: Destination | null) => void;
  allowNone?: boolean;
  id?: string;
}) {
  const { extensions, boxes, steps } = useTargets(tenantId);

  // Encoded as "type:id" so one <select> can express the whole union.
  const encoded = value === null ? ''
    : value.type === 'external' ? `external:${value.number}`
    : value.type === 'hangup' ? 'hangup:'
    : `${value.type}:${value.id}`;

  const decode = (raw: string): Destination | null => {
    if (!raw) return null;
    const [type, rest = ''] = raw.split(':');
    if (type === 'hangup') return { type: 'hangup' };
    if (type === 'external') return { type: 'external', number: rest };
    if (type === 'extension' || type === 'ring_group' || type === 'queue' || type === 'ivr'
        || type === 'voicemail' || type === 'message' || type === 'hours'
        || type === 'caller' || type === 'lookup' || type === 'route') {
      return { type, id: rest };
    }
    return null;
  };

  return (
    <div className="arc-row" style={{ gap: 6 }}>
      <select
        {...(id ? { id } : {})}
        className="arc-select"
        value={encoded.startsWith('external:') ? 'external:' : encoded}
        onChange={(e) => onChange(decode(e.target.value))}
        aria-label="Destination"
      >
        {allowNone && <option value="">None</option>}
        {/*
          Each option names its kind as well as its name. A closed select shows only the chosen
          option's text, so "Main menu" on its own could be an IVR, a queue or a hunt list; the
          group heading that said which is not visible once the list closes (owner, 20 September
          2026).
        */}
        <optgroup label="Extensions">
          {extensions.map((e) => (
            <option key={e.id} value={`extension:${e.id}`}>
              Extension · {e.number} {e.display_name}
            </option>
          ))}
        </optgroup>
        {GROUPS.map(([type, group, one]) => steps[type].length > 0 && (
          <optgroup key={type} label={group}>
            {steps[type].map((s) => <option key={s.id} value={`${type}:${s.id}`}>{one} · {s.name}</option>)}
          </optgroup>
        ))}
        {boxes.length > 0 && (
          <optgroup label="Voicemail">
            {boxes.map((b) => (
              <option key={b.id} value={`voicemail:${b.id}`}>Voicemail · {b.extension_number} {b.extension_name}</option>
            ))}
          </optgroup>
        )}
        <optgroup label="Other">
          <option value="external:">External number</option>
          <option value="hangup:">Hang up</option>
        </optgroup>
      </select>

      {value?.type === 'external' && (
        <input
          className="arc-input" style={{ maxWidth: 170 }}
          placeholder="+27821234567" value={value.number}
          onChange={(e) => onChange({ type: 'external', number: e.target.value })}
          aria-label="External number"
        />
      )}
    </div>
  );
}

/** A destination in words: "Hunt list · Sales". The kind first, as the picker shows it. */
export function destinationText(
  d: Destination | null, t: ReturnType<typeof useTargets>,
): string | null {
  if (!d) return null;
  switch (d.type) {
    case 'external': return d.number;
    case 'hangup': return 'Hang up';
    case 'extension': {
      const e = t.extensions.find((x) => x.id === d.id);
      return e ? `Extension · ${e.number} ${e.display_name}` : 'Extension';
    }
    case 'voicemail': {
      const b = t.boxes.find((x) => x.id === d.id);
      return b ? `Voicemail · ${b.extension_number} ${b.extension_name}` : 'Voicemail';
    }
    default: {
      const g = GROUPS.find(([type]) => type === d.type);
      const s = g ? t.steps[g[0]].find((x) => x.id === d.id) : undefined;
      return g ? (s ? `${g[2]} · ${s.name}` : g[2]) : null;
    }
  }
}

/** Human-readable summary of a destination, for tables. */
export function DestinationLabel({ tenantId, destination }: {
  tenantId: string; destination: Destination | null;
}) {
  const targets = useTargets(tenantId);
  if (!destination) return <span className="arc-subtle">not set</span>;
  if (destination.type === 'external') return <span className="arc-mono">{destination.number}</span>;
  if (destination.type === 'hangup') return <span className="arc-subtle">hang up</span>;
  const text = destinationText(destination, targets);
  return text ? <span>{text}</span> : <span className="arc-subtle">—</span>;
}

export { useTargets as useDestinationTargets };
